import { Component, inject, input, OnInit, signal, ChangeDetectionStrategy } from "@angular/core";
import { RepairService } from "../../services/api/repair/repair.service";
import { TableLazyLoadEvent, TableModule } from "@openng/optimus-ui/table";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { SvgIconComponent } from "../../shared/components/svg-icon/svg-icon.component";
import { REPAIR_STATUS } from "../../enums/repairStatus.enum";
import { DatePipe, NgClass, NgTemplateOutlet } from "@angular/common";
import { first } from "rxjs";
import { TooltipModule } from "@openng/optimus-ui/tooltip";
import { RepairStatusPipe } from "../../shared/pipes/repairStatus/repair-status.pipe";
import { Router } from "@angular/router";
import { GateIdentifierPipe } from "../../shared/pipes/gateIdentifier/gate-identifier.pipe";
import { PAGINATOR_CONSTANTS } from "../../constants/paginator";
import { HasPermissionDirective } from "../../shared/directives/has-permission/has-permission.directive";
import { PermissionService } from "../../services/api/permission/permission.service";
import { createTimeoutSignal } from "../../shared/utils/timeout.utils";
import { TimeoutComponent } from "../../shared/components/timeout/timeout.component";
import { Column } from "../../models/ui/tableColumn";
import { RepairRequest } from "../../models/api/fault/repairRequest";

@Component({
  standalone: true,
  selector: "app-repairs",
  imports: [
    TableModule,
    TranslateModule,
    SvgIconComponent,
    NgTemplateOutlet,
    TooltipModule,
    RepairStatusPipe,
    NgClass,
    HasPermissionDirective,
    TimeoutComponent,
    DatePipe,
  ],
  templateUrl: "./repairs.component.html",
  providers: [],
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./repairs.component.scss",
})
export class RepairsComponent implements OnInit {
  public compact = input<boolean>(false);
  public readonly DATE_FORMAT = "dd.MM.yyyy HH:mm:ss";
  public paginatorOptions = PAGINATOR_CONSTANTS;
  public gateIdentifierPipe = inject(GateIdentifierPipe);
  public readonly repairStatus = REPAIR_STATUS;
  private repairService = inject(RepairService);
  public translate = inject(TranslateService);
  public permissions = inject(PermissionService);

  private router = inject(Router);
  private repairRequestLoadSequence = 0;

  public loading = signal(false);
  public timeOut = createTimeoutSignal(this.loading);

  public repairs: RepairRequest[] = [];
  public totalRepairRecords = 0;
  public first = 0;
  public rows = this.paginatorOptions.DEFAULT_ROWS;
  public readonly COMPACT_PAGE_SIZE = 5;

  public COMPACT_COLUMNS: Column[] = [
    {
      field: "timestamp",
      header: this.translate.instant("repairs.table.timestamp"),
      subField: "created_at",
    },
    {
      field: "gateSystem",
      header: this.translate.instant("repairs.table.label"),
    },
    {
      field: "status",
      header: this.translate.instant("repairs.table.status"),
    },
  ];

  public FULL_COLUMNS: Column[] = [
    {
      field: "timestamp",
      header: this.translate.instant("repairs.table.timestamp"),
      subField: "created_at",
    },
    {
      field: "gateSystem",
      header: this.translate.instant("repairs.table.label"),
    },
    {
      field: "status",
      header: this.translate.instant("repairs.table.status"),
    },
    {
      field: "createdBy",
      subField: "name",
      header: this.translate.instant("repairs.table.created_by"),
    },
  ];
  /**
   * Initializes the component by retrieving the first repair request page
   * from the server using the RepairService.
   */
  public ngOnInit() {
    if (this.compact()) {
      this.loadRepairRequests(1, this.COMPACT_PAGE_SIZE, true);
      return;
    }

    this.loadRepairRequests(1, this.rows, false);
  }

  /**
   * Handles the action click event by changing the repair status for the given repair request.
   * @param action - The new status to apply to the repair request.
   * @param repairRequestId - The unique identifier of the repair request to update.
   * After updating the status, it refreshes the list of repair requests to reflect the change.
   * In the event of an error, no specific handling is performed.
   */
  public onActionClick(action: string, repairRequestId: string) {
    this.repairService.changeRepairStatus(action, repairRequestId).subscribe({
      next: () => {
        if (this.compact()) {
          this.loadRepairRequests(1, this.COMPACT_PAGE_SIZE, true);
          return;
        }

        this.loadRepairRequests(this.currentPage, this.rows, false);
      },
    });
  }

  /**
   * Handles lazy loading table trigger and requests the selected page from the backend.
   * @param event The table lazy load event emitted from the table.
   */
  public onLazyLoad(event: TableLazyLoadEvent) {
    const limit = event.rows && event.rows > 0 ? event.rows : this.paginatorOptions.DEFAULT_ROWS;
    const page = Math.floor((event.first ?? 0) / limit) + 1;

    this.first = event.first ?? 0;
    this.rows = limit;
    this.loadRepairRequests(page, limit, false);
  }
  /**
   * Navigates to the detail page for the given repair request.
   * @param repairRequestId - The unique identifier of the repair request to navigate to.
   * Passes the associated repair request object as state to the detail component.
   */
  public onDetailClick(repairRequestId: string) {
    const repairRequest = this.repairs.find((repair) => repair.id === repairRequestId);
    this.router.navigate([`/dashboard/repairs/${repairRequest?.id}`], {
      state: { repairRequest },
    });
  }
  /**
   * Navigate to the Repairs page.
   */
  public goToRepairs() {
    this.router.navigate(["/dashboard/repairs"]);
  }

  private get currentPage() {
    return Math.floor(this.first / this.rows) + 1;
  }

  private loadRepairRequests(page: number, pageSize: number, excludeArchived: boolean) {
    const sequence = ++this.repairRequestLoadSequence;
    this.loading.set(true);
    this.repairService
      .getRepairRequestData({ page, pageSize, excludeArchived })
      .pipe(first())
      .subscribe({
        next: (response) => {
          if (sequence !== this.repairRequestLoadSequence) {
            return;
          }
          this.repairs = response.items;
          this.totalRepairRecords = response.total;
          this.loading.set(false);
        },
        error: () => {
          if (sequence !== this.repairRequestLoadSequence) {
            return;
          }
          this.repairs = [];
          this.totalRepairRecords = 0;
          this.loading.set(false);
        },
      });
  }
}
